// pages/Agents/AgentSpace/AgentSpaceAgentInfoPanel.js
// Right-side drawer content shown alongside the chat (inside AgentSpaceFAB):
//   - Agent header card (name, type, capability summary, connection status)
//   - Activity table (recent workflows for this agent)
//   - Scheduled tasks table (frequency + status + next-run + actions)
// Activity and scheduled tasks refresh whenever `refreshKey` bumps (the parent
// bumps it after a message is sent or a task is scheduled).
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
    Box,
    Typography,
    Skeleton,
    Chip,
    Tooltip,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    InputAdornment,
    Button,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import HistoryIcon from '@mui/icons-material/History';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorIcon from '@mui/icons-material/Error';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import SendIcon from '@mui/icons-material/Send';
import EditIcon from '@mui/icons-material/Edit';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import ArticleIcon from '@mui/icons-material/Article';
import BoltIcon from '@mui/icons-material/Bolt';
import PhoneIcon from '@mui/icons-material/Phone';
import PollIcon from '@mui/icons-material/Poll';
import PeopleIcon from '@mui/icons-material/People';
import ChatIcon from '@mui/icons-material/Chat';
import EventIcon from '@mui/icons-material/Event';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import EmailIcon from '@mui/icons-material/Email';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import axiosInstance from '../../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

// Initial visible items per section. Scrolling up loads/reveals the next batch.
const ACTIVITY_PAGE_SIZE = 4;
const SCHEDULED_PAGE_SIZE = 4;

const colors = {
    textPrimary: '#1e293b',
    textSecondary: '#475569',
    textTertiary: '#94a3b8',
    accent: '#8b5cf6',
    success: '#059669',
    danger: '#dc2626',
    warning: '#f59e0b',
};

const ICON_MAP = {
    PollIcon: (s) => <PollIcon sx={{ fontSize: s }} />,
    PeopleIcon: (s) => <PeopleIcon sx={{ fontSize: s }} />,
    ChatIcon: (s) => <ChatIcon sx={{ fontSize: s }} />,
    EventIcon: (s) => <EventIcon sx={{ fontSize: s }} />,
    MailOutlineIcon: (s) => <MailOutlineIcon sx={{ fontSize: s }} />,
    EmailIcon: (s) => <EmailIcon sx={{ fontSize: s }} />,
    CalendarMonthIcon: (s) => <CalendarMonthIcon sx={{ fontSize: s }} />,
    CleaningServicesIcon: (s) => <CleaningServicesIcon sx={{ fontSize: s }} />,
};

function getIcon(name, size = 18) {
    const fn = ICON_MAP[name];
    return fn ? fn(size) : <AutoAwesomeIcon sx={{ fontSize: size }} />;
}

// Map step action_type to an icon describing what the step actually did.
// Keyword-based so it matches across agent families
// (gmail.list_emails, crm.send_email, survey.sentiment_analysis, etc.)
function getStepIcon(actionType, size = 14) {
    const a = (actionType || '').toLowerCase();
    if (a.includes('send') || a.includes('reply')) return <SendIcon sx={{ fontSize: size }} />;
    if (a.includes('call') || a.includes('phone')) return <PhoneIcon sx={{ fontSize: size }} />;
    if (a.includes('sms') || a.includes('text') || a.includes('message')) return <ChatBubbleOutlineIcon sx={{ fontSize: size }} />;
    if (a.includes('mail') || a.includes('email')) return <MailOutlineIcon sx={{ fontSize: size }} />;
    if (a.includes('calendar') || a.includes('event') || a.includes('schedule')) return <CalendarMonthIcon sx={{ fontSize: size }} />;
    if (a.includes('list') || a.includes('search') || a.includes('find') || a.includes('get') || a.includes('fetch')) return <SearchIcon sx={{ fontSize: size }} />;
    if (a.includes('update') || a.includes('edit') || a.includes('modify')) return <EditIcon sx={{ fontSize: size }} />;
    if (a.includes('create') || a.includes('add') || a.includes('new')) return <AddCircleOutlineIcon sx={{ fontSize: size }} />;
    if (a.includes('delete') || a.includes('trash') || a.includes('remove') || a.includes('clean')) return <DeleteOutlineIcon sx={{ fontSize: size }} />;
    if (a.includes('sentiment') || a.includes('analyz') || a.includes('classif')) return <AnalyticsIcon sx={{ fontSize: size }} />;
    if (a.includes('summar') || a.includes('document') || a.includes('report')) return <ArticleIcon sx={{ fontSize: size }} />;
    return <BoltIcon sx={{ fontSize: size }} />;
}

function formatPast(iso) {
    if (!iso) return 'â€”';
    const d = new Date(iso);
    const diff = Date.now() - d.getTime();
    const min = Math.floor(diff / 60000);
    const hr = Math.floor(diff / 3600000);
    const day = Math.floor(diff / 86400000);
    if (min < 1) return 'Just now';
    if (min < 60) return `${min}m ago`;
    if (hr < 24) return `${hr}h ago`;
    if (day < 7) return `${day}d ago`;
    return d.toLocaleDateString();
}

// Asha-style timestamp for the activity timeline: "May 25, 9:11 AM".
// Adds the year only when it's not the current year so recent rows stay tight.
function formatDateTime(iso) {
    if (!iso) return 'â€”';
    const d = new Date(iso);
    const now = new Date();
    const showYear = d.getFullYear() !== now.getFullYear();
    const datePart = d.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        ...(showYear ? { year: 'numeric' } : {}),
    });
    const timePart = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    return `${datePart}, ${timePart}`;
}

function formatFuture(iso) {
    if (!iso) return 'â€”';
    const d = new Date(iso);
    const diff = d.getTime() - Date.now();
    if (diff <= 0) return 'Due now';
    const min = Math.round(diff / 60000);
    const hr = Math.round(diff / 3600000);
    const day = Math.round(diff / 86400000);
    if (min < 60) return `in ${min}m`;
    if (hr < 24) return `in ${hr}h`;
    if (day < 14) return `in ${day}d`;
    return d.toLocaleDateString();
}

// Pick the most user-meaningful one-line label out of a result item.
function itemLabel(item) {
    if (item === null || item === undefined) return '';
    if (typeof item !== 'object') return String(item).slice(0, 120);
    for (const key of ['title', 'name', 'subject', 'label', 'email', 'session_title', 'invoice_number', 'event_name']) {
        const v = item[key];
        if (typeof v === 'string' && v.trim()) return v.slice(0, 120);
        if (typeof v === 'number') return String(v);
    }
    const entries = Object.entries(item).filter(([, v]) => v !== null && typeof v !== 'object');
    return entries.slice(0, 3).map(([k, v]) => `${k}: ${String(v).slice(0, 40)}`).join(' · ');
}

function itemSubtitle(item) {
    if (typeof item !== 'object' || item === null) return null;
    const parts = [];
    for (const key of ['status', 'sentiment', 'created_at', 'updated_at', 'date', 'last_message_at']) {
        const v = item[key];
        if (typeof v === 'string' && v) parts.push(`${key.replace(/_/g, ' ')}: ${v.slice(0, 30)}`);
        else if (typeof v === 'number') parts.push(`${key.replace(/_/g, ' ')}: ${v}`);
    }
    return parts.slice(0, 3).join(' · ') || null;
}

// Render the truncated raw payload from a step as a compact preview.
function renderStepResults(data) {
    if (data === null || data === undefined) return null;

    let items = null;
    let total = null;
    let truncated = false;
    if (Array.isArray(data)) {
        items = data;
        total = data.length;
    } else if (typeof data === 'object') {
        if (Array.isArray(data.items)) {
            items = data.items;
            total = data._items_total != null ? data._items_total : (data.total != null ? data.total : items.length);
            truncated = data._items_truncated != null
                ? data._items_truncated
                : (data.truncated != null ? data.truncated : items.length < (total || 0));
        }
    }

    if (items) {
        if (items.length === 0) {
            return (
                <Typography sx={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: '0.66rem', color: '#94a3b8' }}>
                    (no items)
                </Typography>
            );
        }
        return (
            <Box sx={{
                bgcolor: 'rgba(0,0,0,0.02)',
                border: '1px solid rgba(0,0,0,0.06)',
                borderRadius: '8px',
                px: 1, py: 0.6,
            }}>
                <Typography sx={{
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    fontSize: '0.58rem', fontWeight: 700, color: '#94a3b8',
                    textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.4,
                }}>
                    Results {total ? `· ${total} total` : ''}
                </Typography>
                <Box component="ol" sx={{ m: 0, pl: 2.5, display: 'flex', flexDirection: 'column', gap: 0.4 }}>
                    {items.map((it, i) => {
                        const lbl = itemLabel(it);
                        const sub = itemSubtitle(it);
                        return (
                            <Box component="li" key={i} sx={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                                <Typography sx={{
                                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                                    fontSize: '0.68rem', color: '#1e293b', lineHeight: 1.4,
                                }}>
                                    {lbl || '(unnamed item)'}
                                </Typography>
                                {sub && (
                                    <Typography sx={{
                                        fontFamily: "'Plus Jakarta Sans', sans-serif",
                                        fontSize: '0.6rem', color: '#94a3b8',
                                    }}>
                                        {sub}
                                    </Typography>
                                )}
                            </Box>
                        );
                    })}
                </Box>
                {truncated && total && (
                    <Typography sx={{
                        fontFamily: "'Plus Jakarta Sans', sans-serif",
                        fontSize: '0.58rem', color: '#94a3b8', mt: 0.4, fontStyle: 'italic',
                    }}>
                        Showing the first {items.length} of {total} — ask the agent to filter for more.
                    </Typography>
                )}
            </Box>
        );
    }

    let text;
    try {
        text = typeof data === 'string' ? data : JSON.stringify(data, null, 2);
    } catch (e) {
        text = String(data);
    }
    if (text.length > 800) text = text.slice(0, 800) + '…';
    return (
        <Box sx={{
            bgcolor: 'rgba(0,0,0,0.02)',
            border: '1px solid rgba(0,0,0,0.06)',
            borderRadius: '8px',
            px: 1, py: 0.6,
            maxHeight: 180, overflow: 'auto',
        }}>
            <Typography sx={{
                fontFamily: "'Fira Code', monospace",
                fontSize: '0.62rem', color: '#475569',
                whiteSpace: 'pre-wrap', wordBreak: 'break-word',
            }}>
                {text}
            </Typography>
        </Box>
    );
}

export default function AgentSpaceAgentInfoPanel({ chatbotId, agentType, refreshKey, liveProcessing = false, onClose }) {
    const [agentDef, setAgentDef] = useState(null);
    const [loadingDef, setLoadingDef] = useState(true);
    const [connectionStatus, setConnectionStatus] = useState(null);
    const [activity, setActivity] = useState([]);
    const [activityLoading, setActivityLoading] = useState(true);
    const [activityPage, setActivityPage] = useState(1);
    const [activityHasMore, setActivityHasMore] = useState(false);
    const [activityLoadingMore, setActivityLoadingMore] = useState(false);
    const activityScrollRef = useRef(null);
    // Captured before we prepend older items so we can restore scroll position.
    const prevActivityScrollRef = useRef(null);

    // Track which task cards have been expanded to show their full step list.
    const [expandedTasks, setExpandedTasks] = useState(() => new Set());
    const toggleExpand = (taskId) => {
        setExpandedTasks((prev) => {
            const next = new Set(prev);
            if (next.has(taskId)) next.delete(taskId);
            else next.add(taskId);
            return next;
        });
    };
    const [scheduled, setScheduled] = useState([]);
    const [scheduledLoading, setScheduledLoading] = useState(true);
    // Client-side pagination for scheduled tasks (backend returns all at once).
    const [scheduledVisible, setScheduledVisible] = useState(SCHEDULED_PAGE_SIZE);
    const scheduledScrollRef = useRef(null);

    // Activity filters (Asha-style workflows table)
    const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'in_progress' | 'completed'
    const [search, setSearch] = useState('');

    useEffect(() => {
        if (!chatbotId || !agentType) return;
        let cancelled = false;
        setLoadingDef(true);
        Promise.all([
            axiosInstance.get('/api/agent-space/agent-types'),
            axiosInstance.get(`/api/agent-space/connections?chatbot_id=${chatbotId}`),
        ])
            .then(([typesRes, connRes]) => {
                if (cancelled) return;
                const def = (typesRes.data?.data || []).find((a) => a.id === agentType);
                setAgentDef(def || null);
                const statuses = connRes.data?.data || {};
                const connKey = def?.connection_type || agentType;
                setConnectionStatus(statuses[connKey] || null);
            })
            .catch(() => { /* fall through */ })
            .finally(() => { if (!cancelled) setLoadingDef(false); });
        return () => { cancelled = true; };
    }, [chatbotId, agentType]);

    // Paginated fetch. `append=true` means we're loading OLDER activities and
    // appending them to the existing newest-first array (which becomes
    // "prepending" once we reverse for display in tasksChronological).
    // Three call modes:
    //   - append=true  → load OLDER page (pagination), push to end of array
    //   - silent=true  → background poll fetching page 1. MUST NOT wipe
    //     older pages the user already loaded. We merge new items in:
    //     update what came back (preserving identity if unchanged), prepend
    //     genuinely-new items, keep everything else as-is.
    //   - else (silent=false, append=false) → explicit refresh. Replace.
    const fetchActivity = useCallback((page = 1, append = false, silent = false) => {
        if (!chatbotId || !agentType) return;
        if (!silent) {
            if (!append) setActivityLoading(true);
            else setActivityLoadingMore(true);
        }
        axiosInstance.get('/api/agent-space/dashboard/activities', {
            params: { chatbot_id: chatbotId, agent_type: agentType, page, per_page: ACTIVITY_PAGE_SIZE },
        })
            .then((res) => {
                if (!res.data?.success) return;
                const items = res.data.data?.activities || [];
                setActivity((prev) => {
                    if (append) return [...prev, ...items];
                    if (!prev || prev.length === 0) return items;

                    const sameShape = (a, b) => (
                        (a.metadata?.step_count || 0) === (b.metadata?.step_count || 0)
                        && (a.metadata?.turn_count || 0) === (b.metadata?.turn_count || 0)
                        && (a.metadata?.file_count || 0) === (b.metadata?.file_count || 0)
                        && a.status === b.status
                        && a.timestamp === b.timestamp
                    );

                    if (silent) {
                        // MERGE mode — never drop older pages.
                        const newById = new Map(items.map((n) => [n.id, n]));
                        const seenIds = new Set();
                        const merged = prev.map((p) => {
                            const next = newById.get(p.id);
                            if (!next) return p;
                            seenIds.add(p.id);
                            return sameShape(p, next) ? p : next;
                        });
                        const brandNew = items.filter((n) => !seenIds.has(n.id));
                        return brandNew.length > 0 ? [...brandNew, ...merged] : merged;
                    }

                    // Non-silent explicit refresh — replace, but reuse old
                    // object refs where shape is unchanged.
                    const prevById = new Map(prev.map((p) => [p.id, p]));
                    return items.map((next) => {
                        const old = prevById.get(next.id);
                        return old && sameShape(old, next) ? old : next;
                    });
                });
                // Don't shrink hasMore/page on silent polls — page 1 always
                // reports has_more=true even after the user is on page 3+,
                // and the page counter should reflect the deepest page
                // they've loaded, not the page we just polled.
                if (!silent) {
                    setActivityHasMore(!!res.data.data?.has_more);
                    setActivityPage(res.data.data?.page || page);
                } else if (append) {
                    setActivityHasMore(!!res.data.data?.has_more);
                }
            })
            .catch(() => { if (!append && !silent) setActivity([]); })
            .finally(() => {
                if (!silent) {
                    setActivityLoading(false);
                    setActivityLoadingMore(false);
                }
            });
    }, [chatbotId, agentType]);

    // Scroll handler: when the user scrolls near the top of the activity
    // section, load the next (older) page. Capture scroll metrics first so
    // we can restore the user's view position after the older items prepend.
    const handleActivityScroll = useCallback((e) => {
        if (activityLoadingMore || activityLoading || !activityHasMore) return;
        if (e.target.scrollTop <= 32) {
            prevActivityScrollRef.current = {
                height: e.target.scrollHeight,
                top: e.target.scrollTop,
            };
            fetchActivity(activityPage + 1, true);
        }
    }, [activityLoadingMore, activityLoading, activityHasMore, activityPage, fetchActivity]);

    // Manual "Load previous activity" click — same path as the scroll-up
    // auto-loader, with scroll preservation so the user keeps their place.
    const handleLoadOlder = useCallback(() => {
        if (activityLoadingMore || activityLoading || !activityHasMore) return;
        const container = activityScrollRef.current;
        if (container) {
            prevActivityScrollRef.current = {
                height: container.scrollHeight,
                top: container.scrollTop,
            };
        }
        fetchActivity(activityPage + 1, true);
    }, [activityLoadingMore, activityLoading, activityHasMore, activityPage, fetchActivity]);

    // Smart scroll handling. Three cases:
    //   1. Older-page prepend → restore the user's previous scroll position
    //      so the page they were reading doesn't jump
    //   2. Very first load → scroll to bottom (latest task)
    //   3. Silent poll update (new step arrives, or new session appended) →
    //      ONLY scroll to bottom when the user was already pinned at the
    //      bottom (within ~80px). Otherwise leave their scroll alone so they
    //      can read older entries without getting yanked back to live.
    const lastActivityLenRef = useRef(0);
    const initialLoadDoneRef = useRef(false);
    useEffect(() => {
        const container = activityScrollRef.current;
        if (!container) return;
        if (prevActivityScrollRef.current) {
            const { height: prevHeight, top: prevTop } = prevActivityScrollRef.current;
            const diff = container.scrollHeight - prevHeight;
            if (diff > 0) container.scrollTop = prevTop + diff;
            prevActivityScrollRef.current = null;
        } else if (!initialLoadDoneRef.current && activityPage === 1 && activity.length > 0) {
            // First load — show the latest entry.
            container.scrollTop = container.scrollHeight;
            initialLoadDoneRef.current = true;
        } else if (activity.length !== lastActivityLenRef.current) {
            // Length changed (silent poll added/removed a session). If the
            // user was at the bottom, follow new content. Otherwise stay put.
            const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
            if (distanceFromBottom < 80) {
                container.scrollTop = container.scrollHeight;
            }
        }
        lastActivityLenRef.current = activity.length;
    }, [activity, activityPage]);

    const fetchScheduled = useCallback(() => {
        if (!chatbotId || !agentType) return;
        setScheduledLoading(true);
        axiosInstance.get('/api/agent-space/scheduled-tasks', {
            params: { chatbot_id: chatbotId, agent_type: agentType },
        })
            .then((res) => { if (res.data?.success) setScheduled(res.data.data || []); })
            .catch(() => setScheduled([]))
            .finally(() => setScheduledLoading(false));
    }, [chatbotId, agentType]);

    useEffect(() => {
        // Reset pagination on agent change / refresh, then load page 1.
        setActivityPage(1);
        setActivityHasMore(false);
        fetchActivity(1, false);
    }, [fetchActivity, refreshKey]);
    useEffect(() => {
        setScheduledVisible(SCHEDULED_PAGE_SIZE);
        fetchScheduled();
    }, [fetchScheduled, refreshKey]);

    // Live polling while a chat message is in flight. The engine writes
    // step rows incrementally (running → completed) so polling the activity
    // endpoint every ~2s lets the user watch the agent work in real time.
    // `silent=true` skips the loading-skeleton toggle so the visible list
    // doesn't unmount between polls (no more flicker).
    useEffect(() => {
        if (!liveProcessing || !chatbotId || !agentType) return;
        const intervalId = setInterval(() => {
            fetchActivity(1, false, true);
        }, 2000);
        return () => clearInterval(intervalId);
    }, [liveProcessing, chatbotId, agentType, fetchActivity]);

    // Background polling for scheduled-task runs. The scheduler executes
    // tasks every minute (or more often for minutely/hourly intervals) and
    // writes into the same activity tables. A 10-second poll catches those
    // runs as soon as they begin without burning bandwidth on idle pages.
    // Skipped while a live chat is in flight (the 2s loop above already
    // covers that scenario at finer granularity).
    useEffect(() => {
        if (liveProcessing || !chatbotId || !agentType) return;
        const intervalId = setInterval(() => {
            if (document.visibilityState === 'visible') {
                fetchActivity(1, false, true);
            }
        }, 10000);
        return () => clearInterval(intervalId);
    }, [liveProcessing, chatbotId, agentType, fetchActivity]);

    // Scheduled tasks: reveal more on scroll-up near the top.
    const handleScheduledScroll = useCallback((e) => {
        if (e.target.scrollTop <= 32 && scheduledVisible < scheduled.length) {
            setScheduledVisible((v) => Math.min(v + SCHEDULED_PAGE_SIZE, scheduled.length));
        }
    }, [scheduled.length, scheduledVisible]);

    // Show one card per SESSION (full conversation), ordered oldest → newest
    // so the latest session sits at the bottom of the panel. Each card lists
    // the chronological turns (user prompt → agent reply with steps → next
    // user follow-up → next agent reply, etc.).
    const tasksChronological = useMemo(() => {
        return [...activity].reverse().map((act) => ({
            id: act.id,
            session_code: act.metadata?.session_code,
            title: act.resource_name || 'Untitled task',
            description: act.description,
            status: act.status,
            timestamp: act.timestamp,
            // Legacy flat step list (kept for the "+ N more steps" expander).
            steps: act.step_details || [],
            stepCount: act.metadata?.step_count || (act.step_details || []).length,
            // NEW: per-turn conversation.
            turns: act.turns || [],
            turnCount: act.metadata?.turn_count || (act.turns || []).length,
            // NEW: files spilled to GCS by the engine, surfaced as chips
            // under the conversation.
            files: act.files || [],
            fileCount: act.metadata?.file_count || (act.files || []).length,
        }));
    }, [activity]);

    const activityCounts = useMemo(() => ({
        all: tasksChronological.length,
        completed: tasksChronological.filter((t) => t.status === 'completed').length,
        failed: tasksChronological.filter((t) => t.status === 'failed').length,
    }), [tasksChronological]);

    const filteredActivity = useMemo(() => {
        let rows = tasksChronological;
        if (statusFilter === 'completed') rows = rows.filter((t) => t.status === 'completed');
        else if (statusFilter === 'failed') rows = rows.filter((t) => t.status === 'failed');
        if (search.trim()) {
            const q = search.trim().toLowerCase();
            rows = rows.filter((t) =>
                (t.title || '').toLowerCase().includes(q)
                || (t.description || '').toLowerCase().includes(q)
                || (t.steps || []).some((s) =>
                    (s.label || '').toLowerCase().includes(q)
                    || (s.output_preview || '').toLowerCase().includes(q)
                )
            );
        }
        return rows;
    }, [tasksChronological, statusFilter, search]);

    const toggleScheduled = async (task) => {
        const next = task.status === 'active' ? 'paused' : 'active';
        try {
            const resp = await axiosInstance.patch(`/api/agent-space/scheduled-tasks/${task.id}`, { status: next });
            if (resp.data?.success) {
                setScheduled((prev) => prev.map((t) => (t.id === task.id ? resp.data.data : t)));
            }
        } catch (e) { console.error(e); }
    };

    const deleteScheduled = async (task) => {
        if (!window.confirm('Delete this scheduled task?')) return;
        try {
            const resp = await axiosInstance.delete(`/api/agent-space/scheduled-tasks/${task.id}`);
            if (resp.data?.success) setScheduled((prev) => prev.filter((t) => t.id !== task.id));
        } catch (e) { console.error(e); }
    };

    if (!agentType) {
        return (
            <Box sx={{ p: 2, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
                <Box>
                    <AutoAwesomeIcon sx={{ fontSize: 32, color: 'rgba(0,0,0,0.12)', mb: 1 }} />
                    <Typography sx={{ fontFamily, fontSize: '0.78rem', color: colors.textTertiary }}>
                        Pick an agent on the left to see its summary, activity and scheduled tasks here.
                    </Typography>
                </Box>
            </Box>
        );
    }

    const agentColor = agentDef?.color || colors.accent;
    const isConfigured = agentDef
        ? (agentDef.requires_connection ? !!connectionStatus?.is_connected : true)
        : false;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 1.5, p: 1.75, overflow: 'auto', fontFamily, position: 'relative' }}>
            {/* Floating close button at the panel's top-right corner.
                Placed at the panel level (not inside the agent card) so it
                never overlaps the status chip or other content. */}
            {onClose && (
                <Tooltip title="Close">
                    <IconButton
                        size="small"
                        onClick={onClose}
                        sx={{
                            position: 'absolute', top: 8, right: 10, zIndex: 2,
                            color: colors.textTertiary,
                            bgcolor: 'rgba(255,255,255,0.85)',
                            border: '1px solid rgba(0,0,0,0.06)',
                            '&:hover': { color: colors.textPrimary, bgcolor: '#fff' },
                        }}
                    >
                        <CloseIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                </Tooltip>
            )}
            {/* Agent header card */}
            {loadingDef ? (
                <Skeleton variant="rounded" height={120} sx={{ borderRadius: '12px' }} />
            ) : (
                <Box sx={{
                    p: 2, borderRadius: '12px',
                    bgcolor: `${agentColor}08`,
                    border: `1px solid ${agentColor}26`,
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1, pr: onClose ? 4.5 : 0 }}>
                        <Box sx={{
                            width: 36, height: 36, borderRadius: '10px',
                            bgcolor: `${agentColor}18`, color: agentColor,
                            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                        }}>
                            {getIcon(agentDef?.icon_name, 20)}
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ fontFamily, fontSize: '0.92rem', fontWeight: 700, color: colors.textPrimary }} noWrap>
                                {agentDef?.label || agentType}
                            </Typography>
                            <Typography sx={{ fontFamily, fontSize: '0.68rem', color: colors.textTertiary }} noWrap>
                                {agentDef?.requires_connection ? 'Connection-based agent' : 'Resource agent'}
                            </Typography>
                        </Box>
                        <Chip
                            size="small"
                            icon={isConfigured
                                ? <CheckCircleOutlineIcon sx={{ fontSize: '14px !important', color: `${colors.success} !important` }} />
                                : <ErrorOutlineIcon sx={{ fontSize: '14px !important', color: '#f59e0b !important' }} />}
                            label={isConfigured ? (connectionStatus?.connected_email ? 'Connected' : 'Ready') : 'Not configured'}
                            sx={{
                                fontFamily, fontSize: '0.58rem', fontWeight: 700,
                                height: 20,
                                color: isConfigured ? colors.success : '#f59e0b',
                                bgcolor: isConfigured ? 'rgba(5,150,105,0.08)' : 'rgba(245,158,11,0.08)',
                                border: `1px solid ${isConfigured ? 'rgba(5,150,105,0.18)' : 'rgba(245,158,11,0.18)'}`,
                                '& .MuiChip-icon': { ml: '4px' },
                            }}
                        />
                    </Box>
                    <Typography sx={{ fontFamily, fontSize: '0.72rem', color: colors.textSecondary, lineHeight: 1.5 }}>
                        {agentDef?.capability_description || agentDef?.description || 'Agent description unavailable.'}
                    </Typography>
                    {connectionStatus?.connected_email && (
                        <Typography sx={{ fontFamily, fontSize: '0.62rem', color: colors.textTertiary, mt: 0.75 }} noWrap>
                            {connectionStatus.connected_email}
                        </Typography>
                    )}
                </Box>
            )}

            {/* Activity (Asha-style timeline) */}
            <Box>
                {/* Header with tabs + count */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.25 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Typography sx={{ fontFamily, fontSize: '0.95rem', fontWeight: 700, color: colors.textPrimary }}>
                            Activity
                        </Typography>
                        {liveProcessing && (
                            <Box
                                sx={{
                                    display: 'inline-flex', alignItems: 'center', gap: 0.4,
                                    px: 0.75, py: 0.15, borderRadius: '5px',
                                    bgcolor: 'rgba(5,150,105,0.12)',
                                    border: '1px solid rgba(5,150,105,0.2)',
                                }}
                            >
                                <Box sx={{
                                    width: 6, height: 6, borderRadius: '50%',
                                    bgcolor: colors.success,
                                    animation: 'pulse-live 1.2s ease-in-out infinite',
                                    '@keyframes pulse-live': {
                                        '0%, 100%': { opacity: 0.5 },
                                        '50%': { opacity: 1 },
                                    },
                                }} />
                                <Typography sx={{ fontFamily, fontSize: '0.58rem', fontWeight: 700, color: colors.success, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                    Live
                                </Typography>
                            </Box>
                        )}
                    </Box>
                    <Typography sx={{ fontFamily, fontSize: '0.65rem', color: colors.textTertiary }}>
                        {activityCounts.all} task{activityCounts.all !== 1 ? 's' : ''}
                        {activityCounts.failed > 0 && ` Â· ${activityCounts.failed} failed`}
                    </Typography>
                </Box>


                {/* One card per TASK (oldest â†’ newest, so latest sits at the
                    bottom). Each card lists the step-by-step summary of what
                    the agent actually did inside that task â€” the natural
                    language output of each step, not technical labels. */}
                {activityLoading ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {[1, 2, 3].map((i) => <Skeleton key={i} variant="rounded" height={100} sx={{ borderRadius: '12px' }} />)}
                    </Box>
                ) : filteredActivity.length === 0 ? (
                    <Box sx={{ py: 3, textAlign: 'center', borderRadius: '10px', bgcolor: 'rgba(0,0,0,0.02)' }}>
                        <AutoAwesomeIcon sx={{ fontSize: 28, color: 'rgba(0,0,0,0.12)', mb: 0.5 }} />
                        <Typography sx={{ fontFamily, fontSize: '0.72rem', color: colors.textTertiary }}>
                            {search ? 'No tasks match your search.' : statusFilter !== 'all' ? `No ${statusFilter} tasks.` : 'No activity yet for this agent.'}
                        </Typography>
                    </Box>
                ) : (
                    <Box
                        ref={activityScrollRef}
                        onScroll={handleActivityScroll}
                        sx={{
                            display: 'flex', flexDirection: 'column', gap: 1,
                            maxHeight: 420,
                            overflowY: 'auto',
                            pr: 0.5,
                            '&::-webkit-scrollbar': { width: '6px' },
                            '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.12)', borderRadius: '4px' },
                        }}
                    >
                        {/* Top-of-list pager. Three states (mutually exclusive):
                              - hasMore + idle  → clickable "Load previous activity"
                                button, centered. Same behavior as scrolling to the
                                top, but discoverable + tappable.
                              - loadingMore     → button shows a spinner + "Loading…"
                              - !hasMore (and we've already loaded multiple pages)
                                                → "Beginning of history" anchor */}
                        {activityHasMore ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 0.5 }}>
                                <Box
                                    onClick={handleLoadOlder}
                                    role="button"
                                    sx={{
                                        display: 'inline-flex', alignItems: 'center', gap: 0.6,
                                        px: 1.5, py: 0.55,
                                        borderRadius: '999px',
                                        bgcolor: '#fff',
                                        border: `1px solid ${agentColor}40`,
                                        color: agentColor,
                                        cursor: activityLoadingMore ? 'progress' : 'pointer',
                                        opacity: activityLoadingMore ? 0.7 : 1,
                                        pointerEvents: activityLoadingMore ? 'none' : 'auto',
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                                        transition: 'all 0.18s ease',
                                        '&:hover': {
                                            bgcolor: `${agentColor}0a`,
                                            borderColor: `${agentColor}66`,
                                            boxShadow: `0 4px 12px ${agentColor}24`,
                                            transform: 'translateY(-1px)',
                                        },
                                    }}
                                >
                                    {activityLoadingMore ? (
                                        <Box sx={{
                                            width: 12, height: 12, borderRadius: '50%',
                                            border: `1.5px solid ${agentColor}40`,
                                            borderTopColor: agentColor,
                                            animation: 'agentLoadSpin 0.8s linear infinite',
                                            '@keyframes agentLoadSpin': {
                                                from: { transform: 'rotate(0deg)' },
                                                to: { transform: 'rotate(360deg)' },
                                            },
                                        }} />
                                    ) : (
                                        <KeyboardArrowUpIcon sx={{ fontSize: 14 }} />
                                    )}
                                    <Typography sx={{ fontFamily, fontSize: '0.68rem', fontWeight: 700 }}>
                                        {activityLoadingMore ? 'Loading…' : 'Load previous activity'}
                                    </Typography>
                                </Box>
                            </Box>
                        ) : (
                            filteredActivity.length > ACTIVITY_PAGE_SIZE && (
                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 0.4, py: 0.5 }}>
                                    <HistoryIcon sx={{ fontSize: 12, color: colors.textTertiary }} />
                                    <Typography sx={{ fontFamily, fontSize: '0.6rem', color: colors.textTertiary, fontWeight: 600 }}>
                                        Beginning of history
                                    </Typography>
                                </Box>
                            )
                        )}
                        {filteredActivity.map((task) => {
                            const isCompleted = task.status === 'completed';
                            const isFailed = task.status === 'failed';
                            const sc = isCompleted ? colors.success : isFailed ? colors.danger : agentColor;
                            const statusBg = isCompleted ? 'rgba(5,150,105,0.1)' : isFailed ? 'rgba(220,38,38,0.1)' : `${agentColor}14`;
                            const statusLabel = isCompleted ? 'Completed' : isFailed ? 'Failed' : 'In Progress';
                            return (
                                <Box
                                    key={task.id}
                                    sx={{
                                        p: 1.5, borderRadius: '12px',
                                        bgcolor: 'rgba(255,255,255,0.85)',
                                        border: '1px solid rgba(0,0,0,0.06)',
                                        borderLeft: `3px solid ${sc}`,
                                        transition: 'all 0.15s ease',
                                        '&:hover': { borderColor: `${sc}40`, borderLeft: `3px solid ${sc}`, bgcolor: '#fff' },
                                    }}
                                >
                                    {/* Task header: title + status pill + timestamp on the right */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.6 }}>
                                        <Typography sx={{ fontFamily, fontSize: '0.78rem', fontWeight: 700, color: colors.textPrimary, flex: 1 }} noWrap>
                                            {task.title}
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, flexShrink: 0 }}>
                                            <Box sx={{
                                                display: 'inline-flex', alignItems: 'center', gap: 0.3,
                                                px: 0.6, py: 0.1, borderRadius: '5px', bgcolor: statusBg,
                                            }}>
                                                {isCompleted && <CheckCircleIcon sx={{ fontSize: 10, color: sc }} />}
                                                {isFailed && <ErrorIcon sx={{ fontSize: 10, color: sc }} />}
                                                <Typography sx={{ fontFamily, fontSize: '0.58rem', fontWeight: 700, color: sc }}>
                                                    {statusLabel}
                                                </Typography>
                                            </Box>
                                            <Typography sx={{ fontFamily, fontSize: '0.62rem', color: colors.textTertiary, whiteSpace: 'nowrap' }}>
                                                {formatDateTime(task.timestamp)}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    {/* Full SESSION conversation. Each turn shows
                                        either the user's message (chat bubble) or
                                        the agent's step bullets + final reply.
                                        Falls back to flat-step rendering for legacy
                                        activity entries that don't have turns. */}
                                    {(() => {
                                        const isExpanded = expandedTasks.has(task.id);
                                        const kindMeta = {
                                            task_summary: { label: 'Task', icon: <BoltIcon sx={{ fontSize: 9 }} />, color: colors.accent },
                                            thought: { label: 'Thought', icon: <BoltIcon sx={{ fontSize: 9 }} />, color: colors.accent },
                                            tool_call: { label: 'Action', icon: <CheckCircleIcon sx={{ fontSize: 10 }} />, color: colors.success },
                                            error: { label: 'Error', icon: <ErrorIcon sx={{ fontSize: 10 }} />, color: colors.danger },
                                            system_log: { label: 'System', icon: <BoltIcon sx={{ fontSize: 9 }} />, color: colors.textTertiary },
                                            final: { label: 'Outcome', icon: <CheckCircleIcon sx={{ fontSize: 10 }} />, color: colors.success },
                                        };

                                        const renderStepBullet = (s, i, scopedExpanded) => {
                                            const kind = s.kind || 'tool_call';
                                            const stepDone = s.status === 'completed';
                                            const stepFailed = s.status === 'failed' || kind === 'error';
                                            const stepRunning = s.status === 'running';
                                            const stepColor = stepFailed
                                                ? colors.danger
                                                : stepDone
                                                    ? (kindMeta[kind]?.color || colors.success)
                                                    : colors.warning;
                                            const meta = kindMeta[kind] || kindMeta.tool_call;
                                            const primary = (kind === 'tool_call'
                                                ? (s.output_summary || s.label || s.output_preview || 'Action')
                                                : (s.label || s.output_summary || s.output_preview || meta.label));
                                            const stepIcon = stepFailed
                                                ? <ErrorIcon sx={{ fontSize: 10 }} />
                                                : stepDone
                                                    ? (meta.icon || <CheckCircleIcon sx={{ fontSize: 10 }} />)
                                                    : <BoltIcon sx={{ fontSize: 9 }} />;
                                            return (
                                                <Box
                                                    key={s.step_id || i}
                                                    sx={{
                                                        display: 'flex', alignItems: 'flex-start', gap: 0.75,
                                                        // Subtle slide-in so new steps that arrive during a
                                                        // live poll feel additive instead of popping. CSS
                                                        // only — runs once per mount (React reuses DOM nodes
                                                        // for keys it's already seen, so existing rows don't
                                                        // re-animate).
                                                        animation: 'agentStepFadeIn 240ms ease-out both',
                                                        '@keyframes agentStepFadeIn': {
                                                            from: { opacity: 0, transform: 'translateY(4px)' },
                                                            to: { opacity: 1, transform: 'translateY(0)' },
                                                        },
                                                    }}
                                                >
                                                    <Box sx={{
                                                        mt: '4px', width: 14, height: 14, borderRadius: '50%',
                                                        bgcolor: `${stepColor}18`, color: stepColor,
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                                        // Pulse the dot while the step is still running so the
                                                        // "I'm working" feedback is immediately obvious.
                                                        ...(stepRunning ? {
                                                            animation: 'agentStepPulse 1.2s ease-in-out infinite',
                                                            '@keyframes agentStepPulse': {
                                                                '0%, 100%': { boxShadow: `0 0 0 0 ${stepColor}55` },
                                                                '50%': { boxShadow: `0 0 0 4px ${stepColor}00` },
                                                            },
                                                        } : {}),
                                                    }}>
                                                        {stepIcon}
                                                    </Box>
                                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                                        {scopedExpanded && (
                                                            <Typography sx={{
                                                                fontFamily, fontSize: '0.55rem', fontWeight: 700,
                                                                color: stepColor, textTransform: 'uppercase', letterSpacing: '0.4px', lineHeight: 1.2,
                                                            }}>
                                                                {meta.label}
                                                            </Typography>
                                                        )}
                                                        <Typography sx={{
                                                            fontFamily, fontSize: '0.7rem',
                                                            color: stepFailed ? colors.danger : colors.textSecondary,
                                                            fontWeight: stepFailed ? 600 : 400,
                                                            lineHeight: 1.5,
                                                            display: '-webkit-box',
                                                            WebkitLineClamp: scopedExpanded ? 6 : 3, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                                                        }}>
                                                            {primary}
                                                        </Typography>
                                                        {stepFailed && s.hint && (
                                                            <Typography sx={{
                                                                fontFamily, fontSize: '0.65rem', color: colors.textTertiary,
                                                                lineHeight: 1.5, mt: 0.15, fontStyle: 'italic',
                                                            }}>
                                                                {s.hint}
                                                            </Typography>
                                                        )}
                                                        {stepRunning && (
                                                            <Typography sx={{ fontFamily, fontSize: '0.6rem', color: colors.warning, mt: 0.15 }}>
                                                                Running…
                                                            </Typography>
                                                        )}
                                                        {/* Raw results preview — only when expanded. */}
                                                        {scopedExpanded && kind === 'tool_call' && s.output_data && (
                                                            <Box sx={{ mt: 0.5 }}>
                                                                {renderStepResults(s.output_data)}
                                                            </Box>
                                                        )}
                                                    </Box>
                                                </Box>
                                            );
                                        };

                                        // ── Render the FULL SESSION CONVERSATION ──
                                        if (task.turns && task.turns.length > 0) {
                                            return (
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 0.75 }}>
                                                    {task.turns.map((turn, idx) => {
                                                        if (turn.role === 'user') {
                                                            return (
                                                                <Box key={`u-${idx}`} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                                                    <Box sx={{
                                                                        maxWidth: '90%',
                                                                        bgcolor: `${agentColor}10`,
                                                                        border: `1px solid ${agentColor}28`,
                                                                        borderRadius: '10px',
                                                                        px: 1.25, py: 0.75,
                                                                    }}>
                                                                        <Typography sx={{
                                                                            fontFamily, fontSize: '0.58rem', fontWeight: 700,
                                                                            color: agentColor, textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.2,
                                                                        }}>
                                                                            You {turn.timestamp ? `· ${formatDateTime(turn.timestamp)}` : ''}
                                                                        </Typography>
                                                                        <Typography sx={{
                                                                            fontFamily, fontSize: '0.72rem', color: colors.textPrimary,
                                                                            lineHeight: 1.5, whiteSpace: 'pre-wrap',
                                                                        }}>
                                                                            {turn.message || ''}
                                                                        </Typography>
                                                                    </Box>
                                                                </Box>
                                                            );
                                                        }
                                                        // Agent turn
                                                        const turnSteps = turn.step_details || [];
                                                        const defaultKinds = new Set(['tool_call', 'error', 'thought']);
                                                        const visibleTurnSteps = isExpanded
                                                            ? turnSteps
                                                            : turnSteps.filter((s) => defaultKinds.has(s.kind || 'tool_call'));
                                                        return (
                                                            <Box key={`a-${idx}`} sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
                                                                <Typography sx={{
                                                                    fontFamily, fontSize: '0.58rem', fontWeight: 700,
                                                                    color: colors.textTertiary, textTransform: 'uppercase', letterSpacing: '0.5px',
                                                                }}>
                                                                    {agentDef?.label || 'Agent'} {turn.timestamp ? `· ${formatDateTime(turn.timestamp)}` : ''}
                                                                </Typography>
                                                                {visibleTurnSteps.length > 0 && (
                                                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, pl: 0.5 }}>
                                                                        {visibleTurnSteps.map((s, i) => renderStepBullet(s, i, isExpanded))}
                                                                    </Box>
                                                                )}
                                                                {turn.message && (
                                                                    <Typography sx={{
                                                                        fontFamily, fontSize: '0.72rem', color: colors.textPrimary,
                                                                        lineHeight: 1.5, mt: 0.25, whiteSpace: 'pre-wrap',
                                                                        bgcolor: 'rgba(0,0,0,0.025)',
                                                                        border: '1px solid rgba(0,0,0,0.05)',
                                                                        borderRadius: '10px',
                                                                        px: 1.25, py: 0.75,
                                                                    }}>
                                                                        {turn.message}
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                        );
                                                    })}
                                                    {/* Inline expand toggle. */}
                                                    <Box
                                                        onClick={(e) => { e.stopPropagation(); toggleExpand(task.id); }}
                                                        sx={{
                                                            display: 'inline-flex', alignItems: 'center', gap: 0.4,
                                                            mt: 0.25, cursor: 'pointer',
                                                            color: colors.accent, width: 'fit-content',
                                                            '&:hover': { opacity: 0.8 },
                                                        }}
                                                    >
                                                        <Typography sx={{ fontFamily, fontSize: '0.62rem', fontWeight: 600 }}>
                                                            {isExpanded ? 'Show less' : 'Show all step details'}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            );
                                        }

                                        // ── Legacy fallback: flat step list (no turns) ──
                                        const allSteps = task.steps || [];
                                        const defaultKinds = new Set(['tool_call', 'error', 'thought']);
                                        const visibleSteps = isExpanded
                                            ? allSteps
                                            : allSteps.filter((s) => defaultKinds.has(s.kind || 'tool_call'));
                                        const hiddenCount = (task.stepCount || allSteps.length) - visibleSteps.length;

                                        if (visibleSteps.length === 0 && hiddenCount === 0) {
                                            return task.description && (
                                                <Typography sx={{
                                                    fontFamily, fontSize: '0.7rem', color: colors.textSecondary,
                                                    lineHeight: 1.5, mt: 0.5,
                                                }}>
                                                    {task.description}
                                                </Typography>
                                            );
                                        }

                                        return (
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.65, mt: 0.75 }}>
                                                {visibleSteps.map((s, i) => {
                                                    const kind = s.kind || 'tool_call';
                                                    const stepDone = s.status === 'completed';
                                                    const stepFailed = s.status === 'failed' || kind === 'error';
                                                    const stepRunning = s.status === 'running';
                                                    const stepColor = stepFailed
                                                        ? colors.danger
                                                        : stepDone
                                                            ? (kindMeta[kind]?.color || colors.success)
                                                            : colors.warning;
                                                    const meta = kindMeta[kind] || kindMeta.tool_call;

                                                    // Per-kind summary text:
                                                    //   tool_call â†’ output_summary (what came back)
                                                    //   error     â†’ label (mapped friendly message)
                                                    //   task_summary / thought / final / system_log â†’ label
                                                    const primary = (() => {
                                                        if (kind === 'tool_call') {
                                                            return s.output_summary || s.label || s.output_preview || 'Action';
                                                        }
                                                        return s.label || s.output_summary || s.output_preview || meta.label;
                                                    })();

                                                    const stepIcon = stepFailed
                                                        ? <ErrorIcon sx={{ fontSize: 10 }} />
                                                        : stepDone
                                                            ? (meta.icon || <CheckCircleIcon sx={{ fontSize: 10 }} />)
                                                            : <BoltIcon sx={{ fontSize: 9 }} />;

                                                    return (
                                                        <Box key={s.step_id || i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
                                                            <Box sx={{
                                                                mt: '4px',
                                                                width: 14, height: 14, borderRadius: '50%',
                                                                bgcolor: `${stepColor}18`,
                                                                color: stepColor,
                                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                flexShrink: 0,
                                                            }}>
                                                                {stepIcon}
                                                            </Box>
                                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                                {/* Kind label (only shown when expanded, to differentiate task_summary / final / etc.) */}
                                                                {isExpanded && (
                                                                    <Typography sx={{
                                                                        fontFamily, fontSize: '0.55rem', fontWeight: 700,
                                                                        color: stepColor,
                                                                        textTransform: 'uppercase', letterSpacing: '0.4px',
                                                                        lineHeight: 1.2,
                                                                    }}>
                                                                        {meta.label}
                                                                    </Typography>
                                                                )}
                                                                <Typography sx={{
                                                                    fontFamily, fontSize: '0.7rem',
                                                                    color: stepFailed ? colors.danger : colors.textSecondary,
                                                                    fontWeight: stepFailed ? 600 : 400,
                                                                    lineHeight: 1.5,
                                                                    display: '-webkit-box',
                                                                    WebkitLineClamp: isExpanded ? 6 : 3, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                                                                }}>
                                                                    {primary}
                                                                </Typography>
                                                                {stepFailed && s.hint && (
                                                                    <Typography sx={{
                                                                        fontFamily, fontSize: '0.65rem',
                                                                        color: colors.textTertiary,
                                                                        lineHeight: 1.5, mt: 0.15,
                                                                        fontStyle: 'italic',
                                                                    }}>
                                                                        {s.hint}
                                                                    </Typography>
                                                                )}
                                                                {stepFailed && s.details && s.details !== primary && (
                                                                    <Typography sx={{
                                                                        fontFamily, fontSize: '0.6rem',
                                                                        color: colors.textTertiary,
                                                                        lineHeight: 1.4, mt: 0.15,
                                                                        opacity: 0.75,
                                                                        display: '-webkit-box',
                                                                        WebkitLineClamp: isExpanded ? 4 : 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                                                                    }}>
                                                                        {s.details}
                                                                    </Typography>
                                                                )}
                                                                {stepRunning && (
                                                                    <Typography sx={{
                                                                        fontFamily, fontSize: '0.6rem',
                                                                        color: colors.warning, mt: 0.15,
                                                                    }}>
                                                                        Runningâ€¦
                                                                    </Typography>
                                                                )}
                                                                {/* Footer (only when expanded): action_type + duration */}
                                                                {isExpanded && (s.action_type || s.duration_ms) && (
                                                                    <Typography sx={{
                                                                        fontFamily, fontSize: '0.55rem', color: colors.textTertiary,
                                                                        mt: 0.25, letterSpacing: '0.3px',
                                                                    }}>
                                                                        {s.action_type ? s.action_type.toUpperCase() : ''}
                                                                        {s.action_type && s.duration_ms ? ' Â· ' : ''}
                                                                        {s.duration_ms ? (s.duration_ms < 1000 ? `${s.duration_ms}ms` : `${(s.duration_ms / 1000).toFixed(1)}s`) : ''}
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                        </Box>
                                                    );
                                                })}

                                                {/* Expand / collapse toggle. Clickable, with chevron. */}
                                                {(hiddenCount > 0 || isExpanded) && (
                                                    <Box
                                                        onClick={(e) => { e.stopPropagation(); toggleExpand(task.id); }}
                                                        sx={{
                                                            display: 'inline-flex', alignItems: 'center', gap: 0.4,
                                                            mt: 0.25, ml: 2.5,
                                                            cursor: 'pointer',
                                                            color: colors.accent,
                                                            width: 'fit-content',
                                                            '&:hover': { opacity: 0.8 },
                                                        }}
                                                    >
                                                        <Typography sx={{ fontFamily, fontSize: '0.62rem', fontWeight: 600 }}>
                                                            {isExpanded
                                                                ? 'Show less'
                                                                : `+ ${hiddenCount} more step${hiddenCount !== 1 ? 's' : ''}`}
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </Box>
                                        );
                                    })()}

                                    {/* Attachments — files spilled to GCS during
                                        this session (large tool output, generated
                                        reports). Each chip links to the file. */}
                                    {task.files && task.files.length > 0 && (
                                        <Box sx={{
                                            mt: 1, pt: 0.85,
                                            borderTop: '1px dashed rgba(0,0,0,0.08)',
                                            display: 'flex', flexDirection: 'column', gap: 0.4,
                                        }}>
                                            <Typography sx={{
                                                fontFamily, fontSize: '0.54rem', fontWeight: 700,
                                                color: colors.textTertiary, textTransform: 'uppercase', letterSpacing: '0.4px',
                                            }}>
                                                Attachments · {task.files.length}
                                            </Typography>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                {task.files.map((f) => (
                                                    <Box
                                                        key={f.id}
                                                        component="a"
                                                        href={f.file_url || '#'}
                                                        target="_blank"
                                                        rel="noopener"
                                                        onClick={(e) => e.stopPropagation()}
                                                        sx={{
                                                            display: 'inline-flex', alignItems: 'center', gap: 0.4,
                                                            px: 0.75, py: 0.25, borderRadius: '6px',
                                                            textDecoration: 'none',
                                                            bgcolor: `${agentColor}10`,
                                                            border: `1px solid ${agentColor}24`,
                                                            color: agentColor,
                                                            '&:hover': { bgcolor: `${agentColor}18`, borderColor: `${agentColor}44` },
                                                        }}
                                                    >
                                                        <ArticleIcon sx={{ fontSize: 11 }} />
                                                        <Typography sx={{ fontFamily, fontSize: '0.6rem', fontWeight: 600 }}>
                                                            {f.filename || f.label || 'File'}
                                                        </Typography>
                                                    </Box>
                                                ))}
                                            </Box>
                                        </Box>
                                    )}
                                </Box>
                            );
                        })}
                    </Box>
                )}
            </Box>

            {/* Scheduled */}
            <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <EventRepeatIcon sx={{ fontSize: 14, color: colors.accent }} />
                        <Typography sx={{ fontFamily, fontSize: '0.7rem', fontWeight: 700, color: colors.textTertiary, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            Scheduled tasks
                        </Typography>
                    </Box>
                    <Typography sx={{ fontFamily, fontSize: '0.62rem', color: colors.textTertiary }}>
                        {scheduled.length}
                    </Typography>
                </Box>
                {scheduledLoading ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
                        {[1, 2].map((i) => <Skeleton key={i} variant="rounded" height={28} sx={{ borderRadius: '6px' }} />)}
                    </Box>
                ) : scheduled.length === 0 ? (
                    <Box sx={{ py: 1.5, textAlign: 'center', borderRadius: '8px', bgcolor: 'rgba(0,0,0,0.02)' }}>
                        <Typography sx={{ fontFamily, fontSize: '0.66rem', color: colors.textTertiary }}>
                            Nothing scheduled. Use the clock icon next to send.
                        </Typography>
                    </Box>
                ) : (
                    <Box
                        ref={scheduledScrollRef}
                        onScroll={handleScheduledScroll}
                        sx={{
                            borderRadius: '10px', border: '1px solid rgba(0,0,0,0.06)',
                            overflow: 'hidden', bgcolor: 'rgba(255,255,255,0.8)',
                            maxHeight: 300, overflowY: 'auto',
                            '&::-webkit-scrollbar': { width: '6px' },
                            '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.12)', borderRadius: '4px' },
                        }}
                    >
                        {/* Scroll-up indicator for older entries. */}
                        {scheduledVisible < scheduled.length && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 0.5, bgcolor: 'rgba(0,0,0,0.02)' }}>
                                <Typography sx={{ fontFamily, fontSize: '0.58rem', color: colors.textTertiary }}>
                                    Scroll up for olderâ€¦ ({scheduled.length - scheduledVisible} more)
                                </Typography>
                            </Box>
                        )}
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow sx={{ bgcolor: 'rgba(0,0,0,0.02)' }}>
                                    <TableCell sx={{ fontFamily, fontSize: '0.6rem', fontWeight: 700, color: colors.textTertiary, py: 0.5, bgcolor: '#fff' }}>Task</TableCell>
                                    <TableCell sx={{ fontFamily, fontSize: '0.6rem', fontWeight: 700, color: colors.textTertiary, py: 0.5, bgcolor: '#fff' }}>Type</TableCell>
                                    <TableCell sx={{ fontFamily, fontSize: '0.6rem', fontWeight: 700, color: colors.textTertiary, py: 0.5, bgcolor: '#fff' }} align="right">Next</TableCell>
                                    <TableCell sx={{ py: 0.5, bgcolor: '#fff' }} />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {/* Show only the latest `scheduledVisible` rows; user scrolls
                                    up to reveal more. Reversed so newest sits at the bottom. */}
                                {[...scheduled]
                                    .slice(0, scheduledVisible)
                                    .reverse()
                                    .map((t) => {
                                    const isActive = t.status === 'active';
                                    const isOnce = t.frequency === 'once';
                                    const typeColor = isOnce ? colors.warning : colors.accent;
                                    return (
                                        <TableRow key={t.id} hover>
                                            <TableCell sx={{ fontFamily, fontSize: '0.68rem', color: colors.textPrimary, fontWeight: 500, maxWidth: 130, py: 0.65 }}>
                                                <Tooltip title={t.task_message || ''} placement="top">
                                                    <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                        {t.name || t.task_message || `Task #${t.id}`}
                                                    </Box>
                                                </Tooltip>
                                                <Typography sx={{ fontFamily, fontSize: '0.55rem', color: isActive ? colors.success : colors.textTertiary, textTransform: 'capitalize', mt: 0.15 }}>
                                                    {t.status}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{ py: 0.65 }}>
                                                <Chip
                                                    size="small"
                                                    label={isOnce ? 'One-time' : t.frequency}
                                                    sx={{
                                                        fontFamily, fontSize: '0.55rem', height: 16,
                                                        fontWeight: 700, textTransform: 'capitalize',
                                                        bgcolor: `${typeColor}14`, color: typeColor,
                                                        border: `1px solid ${typeColor}28`,
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.6rem', color: colors.textTertiary, py: 0.65 }} align="right">
                                                {formatFuture(t.next_run_at)}
                                            </TableCell>
                                            <TableCell align="right" sx={{ whiteSpace: 'nowrap', py: 0.25, pr: 0.5 }}>
                                                <Tooltip title={isActive ? 'Pause' : 'Resume'}>
                                                    <IconButton size="small" onClick={() => toggleScheduled(t)} sx={{ p: 0.2 }}>
                                                        {isActive
                                                            ? <PauseCircleOutlineIcon sx={{ fontSize: 14, color: colors.textTertiary }} />
                                                            : <PlayCircleOutlineIcon sx={{ fontSize: 14, color: colors.success }} />}
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Delete">
                                                    <IconButton size="small" onClick={() => deleteScheduled(t)} sx={{ p: 0.2 }}>
                                                        <DeleteOutlineIcon sx={{ fontSize: 14, color: colors.danger }} />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </Box>
                )}
            </Box>

        </Box>
    );
}
